import { useState, useRef } from "react";
import { IoClose } from "react-icons/io5";
import { FaEdit } from "react-icons/fa";
import useDisableBackgroundScroll from "../../hooks/useDisableBackgroundScroll";
import AgePicker from "./AgePicker";
import { getProfileImage, CheckIfNullOrUndefined, getMediaUrl } from "../../utils/helpers";
import PhotoEditorModal from "./PhotoEditorModal";
import {
    UpdateProfileDetails, GetPostSignedUrl, uploadFileToSignedUrl
} from "../../services/api";
import { showError } from "../../utils/toast";

export default function EditProfile({ user, onClose, onUpdatedProfile }) {
    const [profileImagePreview, setProfileImagePreview] = useState(getMediaUrl(user.ppImageURL) || "");
    const [profileImage, setProfileImage] = useState(getMediaUrl(user.ppImageURL) || "");
    const [name, setName] = useState(user.name || "");
    const [about, setAbout] = useState(user.about || "");
    const [age, setAge] = useState(user.age || "");
    const [openEditor, setOpenEditor] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [profileImageChanged, setProfileImageChanged] = useState(false);

    const [isSaving, setIsSaving] = useState(false);

    useDisableBackgroundScroll(true);

    const fileInputRef = useRef(null);

    const openFilePicker = () => {
        fileInputRef.current.click();  // trigger hidden input
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setSelectedFile(file);
        setOpenEditor(true);
        e.target.value = "";
    };

    const handleSaveCropped = (croppedBlob) => {

        const previewUrl = URL.createObjectURL(croppedBlob);

        const file = new File([croppedBlob], "profile.jpg", {
            type: croppedBlob.type,
        });

        setProfileImagePreview(previewUrl);
        setProfileImage(file);
        setProfileImageChanged(true);
    };

    const OnProfileSave = async () => {

        if (isSaving) return; // ⛔ prevent double click

        if(CheckIfNullOrUndefined(name))
        {
            showError("Name can not be empty");
            return;
        }

        setIsSaving(true);

        try {
            let imageUrl = profileImage;

            // Upload image ONLY if changed
            if (profileImageChanged) {
                const res = await GetPostSignedUrl();
                const { signedUrl, key, publicUrl } = res.data;

                await uploadFileToSignedUrl(signedUrl, profileImage);

                imageUrl = key;
            }

            const payload = {
                name,
            };

            if (about) {
                payload.about = about;
            }

            if (age) {
                payload.age = age;
            }

            if (profileImageChanged) {
                payload.ppImageURL = imageUrl;
            }

            const updateRes = await UpdateProfileDetails(payload);

            onUpdatedProfile({ profileImagePreview, name, about, age });

            onClose();

        } catch (err) {
            console.error("Edit profile failed", err);
            showError("Something went wrong. Please try again.");
        } finally {
            setIsSaving(false); // ✅ ALWAYS stop loader
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">

            <div className="bg-[#0B0B0B] rounded-2xl p-6 w-[90%] max-w-[480px] max-h-[90vh] overflow-y-auto no-scrollbar">

                {/* Header Row */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold">Edit Profile</h2>

                    <button
                        className="text-white text-3xl"
                        onClick={onClose}
                    >
                        <IoClose />
                    </button>
                </div>

                <div className="flex flex-col md:flex-row items-center md:items-start gap-8">

                    {/* Profile Image */}
                    <div className="relative">
                        <div className="flex justify-center mb-6">
                            <div className="relative w-28 h-28">
                                <img
                                    src={profileImagePreview || getProfileImage(getMediaUrl(user.ppImageURL))}
                                    alt="Profile"
                                    className="w-full h-full rounded-full object-cover border-4 border-red-500"
                                />
                                <div
                                    onClick={openFilePicker}
                                    className="absolute bottom-1 right-1 bg-black/70 p-2 rounded-full border border-white/20">
                                    <FaEdit className="text-white text-sm" />

                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        onChange={handleImageSelect}
                                        className="hidden"
                                    />
                                </div>

                                {openEditor && (
                                    <PhotoEditorModal
                                        file={selectedFile}
                                        onClose={() => setOpenEditor(false)}
                                        onSave={handleSaveCropped}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* PROFILE DETAILS */}
                    <div className="flex-1 w-full">

                        {/* Name */}
                        <div className="mb-2">

                            <div className="flex items-center justify-between">
                                <label className="text-gray-400 text-sm">Name</label>
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span></span>
                                    <span>{name.length} / 12</span>
                                </div>
                            </div>

                            <div>
                                <input
                                    type="text"
                                    maxLength={12}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full bg-transparent border-b border-gray-700 focus:border-red-500 outline-none py-2 text-lg"
                                />
                            </div>
                        </div>

                        {/* About Me */}
                        <div className="mb-2">

                            <div className="flex items-center justify-between">
                                <label className="text-gray-400 text-sm">About Me</label>
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span></span>
                                    <span>{about.length} / 120</span>
                                </div>
                            </div>

                            <div>
                                <textarea
                                    maxLength={120}
                                    value={about}
                                    onChange={(e) => setAbout(e.target.value)}
                                    placeholder="Write a few words about yourself"
                                    className="w-full bg-transparent border-b border-gray-700 focus:border-red-500 outline-none py-2 resize-none h-12"
                                ></textarea>
                            </div>
                        </div>

                        <AgePicker age={age} setAge={setAge} />

                        {/* Save Button */}
                        <button
                            onClick={OnProfileSave}
                            disabled={isSaving}
                            className={`w-full py-3 rounded-full text-lg font-semibold transition
                                ${isSaving
                                    ? "bg-red-500 cursor-not-allowed"
                                    : "bg-red-500 hover:bg-red-500"}
                            `}
                        >
                            {isSaving ? (
                                <div className="flex items-center justify-center gap-2">
                                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    Saving...
                                </div>
                            ) : (
                                "Save"
                            )}
                        </button>

                    </div>

                </div>

            </div>
        </div>
    );
}